import {
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem as MenuItemMui,
  MenuList,
  Popper,
  withStyles,
} from '@material-ui/core';
import React, { useRef } from 'react';
import { useBoolean } from 'react-use-boolean';
import { useKeepSizeAfterRender } from '../../hooks/stylingHooks';
import { ButtonProps, GroupedButton as GroupedButtonBase } from '../common/Button';
import { IconButton } from '../common/IconButton';
import { Loader } from '../common/Loader';
import { Paper } from '../common/Paper';
import { useGalleryContext } from './GalleryContext';
import { getGalleryEditorUrl } from './getGalleryUrl';
import { SaveGalleryModals, useSaveGallery } from './SaveGalleryModals';
import { useGalleryViewStyles } from './galleryViewStyles';
import { ArrowDropDownIcon } from '../../assets/img/ArrowDropDownIcon';

const MenuItem = withStyles({
  root: {
    textTransform: 'uppercase',
    textAlign: 'center',
    display: 'block',
  },
})(MenuItemMui);

const GroupedButton = withStyles<'disabled', any, ButtonProps>((theme) => ({
  disabled: {
    backgroundColor: `${theme.palette.primary.main}!important`,
  },
}))(GroupedButtonBase);

export const SaveGallery: React.FC = () => {
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  useKeepSizeAfterRender(saveButtonRef, { keepWidth: true, keepHeight: true });
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [open, dropdownActions] = useBoolean();
  const { newPanoramas, refetch, currentGallery, currentFolder, waypointsShown } =
    useGalleryContext();
  const { saveAs } = useGalleryViewStyles();
  const saveGallery = useSaveGallery({
    currentGallery,
    currentFolder,
    panoramaDetails: newPanoramas,
    onSaveSuccess: (gallery) => {
      if (currentGallery?.guid === gallery.guid) {
        refetch();
        return;
      }
      window.location.assign(getGalleryEditorUrl(gallery));
    },
    waypointsShown,
  });

  if (!currentFolder) {
    return null;
  }

  const onSaveAsClick = () => {
    dropdownActions.off();
    saveGallery.onSaveAs();
  };

  return (
    <>
      <ButtonGroup disableElevation variant='contained' color='primary' ref={anchorRef}>
        <GroupedButton
          ref={saveButtonRef}
          className={saveAs}
          onClick={saveGallery.onSave}
          aria-label='quick save'
          disabled={saveGallery.isLoading}
          aria-disabled={saveGallery.isLoading}
        >
          {saveGallery.isLoading ? (
            <Loader noMargin loaderColor='white' loaderSize={'2rem'} />
          ) : (
            <>Save</>
          )}
        </GroupedButton>
        <IconButton
          variant='primary'
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label='select save strategy'
          aria-haspopup='menu'
          onClick={dropdownActions.on}
          icon={<ArrowDropDownIcon />}
        />
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement='bottom'
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper style={{ width: anchorRef.current?.clientWidth ?? 'inherit' }}>
              <ClickAwayListener onClickAway={dropdownActions.off}>
                <MenuList>
                  <MenuItem
                    onClick={onSaveAsClick}
                    disabled={saveGallery.isLoading}
                    disableRipple
                    aria-disabled={saveGallery.isLoading}
                  >
                    Save As
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <SaveGalleryModals
        {...saveGallery}
        currentFolder={currentFolder}
        waypointsShown={waypointsShown}
      />
    </>
  );
};
